import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { defaultLanguageFor } from '@org-avp/models'
import { generalStore } from '../../store';
import Loader from '../loader/Loader';
import { COOKIE_KEYS, ENV_TYPES, CONTEXT_RESPONSE, OAUTH_USER_RESPONSE, AVP_LOCAL_HOST } from '../../js/_constants';
import { jwtDecode } from 'jwt-decode';
import helpers, { isDev } from '../../js/helpers';
import { SERVER_CONFIGS } from './../../js/config/clientConfigs'
import { ENV } from '../../js/config/_global';
import type { Brand, Market, KvpsDealerId } from '@org-avp/models';

const isIframeMode = window.parent && window.parent !== window;

const ALLOWED_ORIGINS = [
    AVP_LOCAL_HOST, // localhost 3000, for AVP running locally | please set RCM on port 3001
    SERVER_CONFIGS.audi.dev.avpPortal, // AVP portal | all envs DEV, INT, PROD
    SERVER_CONFIGS.audi.int.avpPortal,
    SERVER_CONFIGS.audi.prod.avpPortal
];

type ContextMessage = {
    type: typeof CONTEXT_RESPONSE;
    brand: Brand;
    market: Market;
    dealerId: KvpsDealerId
};

type OauthUserMessage = {
    type: typeof OAUTH_USER_RESPONSE;
    user: unknown;
};

type IFrameMessage = ContextMessage | OauthUserMessage;

type IFrameRouteProps = {
    component: React.ComponentType<any>;
};

export const IFrameRoute = ({
    component
}: IFrameRouteProps) => {
    const { i18n, t } = useTranslation();
    const [isRouteEnabled, setRouteEnabled] = useState(false);
    const [dealerId, setDealerId] = useState<KvpsDealerId | null>(null);
    const { setAuthenticated, isGlobalLoading, setIsGlobalLoading } = generalStore();
    
    const RouteComponent = component;


    const grpAuthorizeIFrame = (userTokenData) => {
        const currentTimeEpocSec = Date.now() / 1000;
        const jwtDecodedToken = jwtDecode(userTokenData.access_token) as any;
        const duration = jwtDecodedToken.exp - currentTimeEpocSec;
        const hours = duration / 3600;
        // set cookies for auth
        helpers.setCookie(COOKIE_KEYS.TOKEN, userTokenData.access_token, hours);
        helpers.setCookie(COOKIE_KEYS.REFRESH_TOKEN, userTokenData.refresh_token, hours + 4);
        helpers.setCookie(COOKIE_KEYS.ID_TOKEN, userTokenData.id_token);
        setAuthenticated(true);

        // iFrame data received, cookies set, auth done, user data got, now finally show the page/view
        setIsGlobalLoading(false);
    };

    const handleIFrameMessage = (event: MessageEvent<unknown>) => {
        if (!ALLOWED_ORIGINS.includes(event.origin)) {
            console.error('Blocked message from untrusted origin:', event.origin);
            return;
        }

        const msg = event.data as IFrameMessage;

        if (msg?.type === OAUTH_USER_RESPONSE) {
            if (msg.user) {
                const userDataFromAvpIFrame = msg.user
                grpAuthorizeIFrame(userDataFromAvpIFrame);
            }
        }

        if (msg?.type === CONTEXT_RESPONSE) {
            i18n.changeLanguage(defaultLanguageFor(msg.market));
            setDealerId(msg.dealerId);
        }
    };

    // first thing first: get user token for later api calls + device list for a dealer in a specific brand
    useEffect(() => {
        if (isIframeMode) {
            // add class to body RCM to be scrollable inside iFrame (if page is very long)
            document.body.classList.add('is-iframe-scrollable')
            // by default is localhost | for local developer
            let avpOriginUrl = ALLOWED_ORIGINS[0]
            if (!isDev) {
                if (ENV === ENV_TYPES.DEV) avpOriginUrl = ALLOWED_ORIGINS[1] // avp DEV url
                if (ENV === ENV_TYPES.INT) avpOriginUrl = ALLOWED_ORIGINS[2] // avp INT url
                if (ENV === ENV_TYPES.PROD) avpOriginUrl = ALLOWED_ORIGINS[3] // avp PROD url
            }
            // post message to origin, as message to inform it that iFrame is ready
            // this makes the trick!
            // on other AVP iFrame, there are some post messages. here we set a "fake" one.
            window.parent.postMessage(
                { type: 'iframe-ready' },
                // parent origin, meaning AVP url as "parent" for iFrame
                avpOriginUrl
            )
            // listener for AVP user data
            window.addEventListener('message', handleIFrameMessage);
            // show page
            setRouteEnabled(true)
        } else {
            // TODO: set redirect? page is not accessible. only in iFrame, only in AVP
            setIsGlobalLoading(false);
        }
        return () => {
            if (isIframeMode) window.removeEventListener('message', handleIFrameMessage);
        }
    }, []);

    if (isGlobalLoading) return <Loader />;
    
    if (!isRouteEnabled) return <h1 style={{textAlign:'center', marginTop:300}}>{t('page_not_available')}</h1>;

    return (
        <div className="b-i-frame-route" >
            <RouteComponent contextDealerId={dealerId} isIframeMode={isIframeMode} />
        </div>
    );

};

export default IFrameRoute;

